import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from './components/Layout/Layout';
import Calculator from './components/Calculator/Calculator';
import Chart from './components/Chart/Chart';
import { useState } from 'react';
import styles from './App.module.css';
import { calculateInvestmentGrowth } from './utils/calculations';

const CURRENCIES = {
  USD: { 
    symbol: '$', 
    name: 'US Dollar', 
    country: 'United States' 
  },
  EUR: { 
    symbol: '€', 
    name: 'Euro', 
    country: 'European Union' 
  },
  GBP: { 
    symbol: '£', 
    name: 'British Pound', 
    country: 'United Kingdom' 
  },
  JPY: { 
    symbol: '¥', 
    name: 'Japanese Yen', 
    country: 'Japan' 
  },
  KRW: { 
    symbol: '₩', 
    name: 'Korean Won', 
    country: 'South Korea' 
  },
  SAR: { 
    symbol: 'ر.س', 
    name: 'Saudi Riyal', 
    country: 'Saudi Arabia' 
  },
  AED: { 
    symbol: 'د.إ', 
    name: 'UAE Dirham', 
    country: 'United Arab Emirates' 
  },
  CNY: { 
    symbol: '¥', 
    name: 'Chinese Yuan', 
    country: 'China' 
  },
  INR: { 
    symbol: '₹', 
    name: 'Indian Rupee', 
    country: 'India' 
  }
};

function LanguageSelector({ onCurrencyChange, calculatorData }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const languageDefaults = {
    en: { 
      name: 'English', 
      flag: '🇺🇸',
      defaultCurrency: 'USD'
    },
    fr: { 
      name: 'Français', 
      flag: '🇫🇷',
      defaultCurrency: 'EUR'
    },
    ch: { 
      name: '中文', 
      flag: '🇨🇳',
      defaultCurrency: 'CNY'
    },
    ja: { 
      name: '日本語', 
      flag: '🇯🇵',
      defaultCurrency: 'JPY'
    },
    ko: { 
      name: '한국어', 
      flag: '🇰🇷',
      defaultCurrency: 'KRW'
    },
    ar: { 
      name: 'العربية', 
      flag: '🇸🇦',
      defaultCurrency: 'SAR'
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    const newPath = lng === 'en' ? '/' : `/${lng}`;
    navigate(newPath + location.pathname.replace(/^\/(en|fr|ch|ja|ko|ar)?/, ''));
    
    const defaultCurrency = languageDefaults[lng]?.defaultCurrency || 'USD';
    onCurrencyChange({ ...calculatorData, currency: defaultCurrency });
    
    setIsOpen(false);
  };

  const currentLang = languageDefaults[i18n.language] || languageDefaults.en;

  return (
    <div className={styles.languageSelector}>
      <button 
        className={styles.languageButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{currentLang.flag}</span>
        <span>{currentLang.name}</span>
        <span className={styles.arrow}>▼</span>
      </button>
      {isOpen && (
        <div className={styles.dropdown}>
          {Object.entries(languageDefaults).map(([code, { name, flag }]) => (
            <button
              key={code}
              onClick={() => changeLanguage(code)}
              className={styles.languageOption}
            >
              <span>{flag}</span>
              <span>{name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MainContent() {
  const { t } = useTranslation();
  const [calculatorData, setCalculatorData] = useState({
    initialDeposit: 10000,
    monthlyContribution: 1000,
    timeFrame: 10,
    timeUnit: 'years',
    apy: 5,
    currency: 'USD'
  });

  const handleCalculatorChange = (newData) => {
    setCalculatorData(newData);
  };

  const results = calculateInvestmentGrowth(calculatorData);

  return (
    <Layout>
      <LanguageSelector 
        onCurrencyChange={handleCalculatorChange}
        calculatorData={calculatorData}
      />
      <div className={styles.configWithChartContainer}>
        <Calculator 
          data={calculatorData} 
          onChange={handleCalculatorChange}
          currencies={CURRENCIES}
          aria-label="Money Market Investment Calculator Form"
          className={styles.configurationContainer}
        />
        <Chart 
          data={calculatorData}
          currencySymbol={CURRENCIES[calculatorData.currency].symbol}
          aria-label="Money Market Investment Growth Chart"
          className={styles.chartWrapper}
        />
      </div>
      <div className={styles.summaryContainer}>
        <div className={styles.summaryCard}>
          <h3>{t('summary.totalBalance')}</h3>
          <p>
            <span>{CURRENCIES[calculatorData.currency].symbol}</span>
            <span>{results.total.toLocaleString()}</span>
          </p>
        </div>
        <div className={styles.summaryCard}>
          <h3>{t('summary.interestEarned')}</h3>
          <p>
            <span>{CURRENCIES[calculatorData.currency].symbol}</span>
            <span>{results.totalInterest.toLocaleString()}</span>
          </p>
        </div>
        <div className={styles.summaryCard}>
          <h3>{t('summary.totalContributions')}</h3>
          <p>
            <span>{CURRENCIES[calculatorData.currency].symbol}</span>
            <span>{results.totalContributions.toLocaleString()}</span>
          </p>
        </div>
      </div>
    </Layout>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<MainContent />} />
      <Route path="/:lang/*" element={<MainContent />} />
    </Routes>
  );
}

export default App; 